import { useState } from 'react';
import { useSpecifications } from '@/hooks/useSpecifications';
import { JobCategory, SpecificationType } from '@/lib/types/job';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Search, Edit, Trash2, Copy, FileText, FileEdit } from 'lucide-react';
import { SpecificationDialog } from './SpecificationDialog';
import { DeleteSpecificationDialog } from './DeleteSpecificationDialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { format } from 'date-fns';

interface SpecificationsLibraryProps {
  isAdminView?: boolean;
}

export const SpecificationsLibrary = ({ isAdminView = false }: SpecificationsLibraryProps) => {
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<JobCategory | 'all'>('all');
  const [typeFilter, setTypeFilter] = useState<SpecificationType | 'all'>('all');
  const [sortBy, setSortBy] = useState<'name' | 'created_at' | 'usage_count'>('created_at');
  const [editingSpec, setEditingSpec] = useState<any>(null);
  const [deletingSpec, setDeletingSpec] = useState<any>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [viewMode, setViewMode] = useState<'view' | 'edit' | 'create'>('create');

  const { specifications, loading, cloneSpecification, refetch } = useSpecifications({
    category: categoryFilter === 'all' ? undefined : categoryFilter,
    specificationType: typeFilter === 'all' ? undefined : typeFilter,
    search: search || undefined,
  });

  const jobCategories: JobCategory[] = ['print_materials', 'packaging', 'signage', 'merchandising', 'other'];
  const specificationTypes: SpecificationType[] = ['my_organization', 'personal'];

  const getTypeLabel = (type: SpecificationType) => {
    const labels: Record<SpecificationType, string> = {
      my_organization: 'My Organization',
      personal: 'Personal'
    };
    return labels[type];
  };

  const sortedSpecs = [...specifications].sort((a, b) => {
    if (sortBy === 'name') {
      return a.specification_name.localeCompare(b.specification_name);
    } else if (sortBy === 'created_at') {
      return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
    } else {
      return (b.usage_count || 0) - (a.usage_count || 0);
    }
  });

  const handleClone = async (spec: any) => {
    await cloneSpecification(spec.id, `${spec.specification_name} (Copy)`);
  };

  const handleRowClick = (spec: any) => {
    setEditingSpec(spec);
    setViewMode('view');
    setIsDialogOpen(true);
  };

  const handleEdit = (spec: any, e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingSpec(spec);
    setViewMode('edit');
    setIsDialogOpen(true);
  };

  const handleCreate = () => {
    setEditingSpec(null);
    setViewMode('create');
    setIsDialogOpen(true);
  };

  const handleDialogClose = (open: boolean) => {
    setIsDialogOpen(open);
    if (!open) {
      refetch();
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Specifications</h1>
          <p className="text-muted-foreground mt-1">Create and manage reusable specifications for your jobs</p>
        </div>
        <Button onClick={handleCreate} className="gap-2">
          <Plus className="h-4 w-4" />
          Create New Specification
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Filters</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search specifications..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
              />
            </div>

            <Select value={categoryFilter} onValueChange={(v) => setCategoryFilter(v as any)}>
              <SelectTrigger>
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {jobCategories.map((cat) => (
                  <SelectItem key={cat} value={cat}>
                    {cat.replace('_', ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={typeFilter} onValueChange={(v) => setTypeFilter(v as any)}>
              <SelectTrigger>
                <SelectValue placeholder="All Types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                {specificationTypes.map((type) => (
                  <SelectItem key={type} value={type}>
                    {getTypeLabel(type)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={sortBy} onValueChange={(v) => setSortBy(v as any)}>
              <SelectTrigger>
                <SelectValue placeholder="Sort By" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="created_at">Date Created</SelectItem>
                <SelectItem value="name">Name</SelectItem>
                <SelectItem value="usage_count">Usage Count</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <Card>
          <CardContent className="py-12">
            <div className="flex items-center justify-center">
              <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
            </div>
          </CardContent>
        </Card>
      ) : sortedSpecs.length === 0 ? (
        <Card>
          <CardContent className="py-12">
            <div className="text-center space-y-4">
              <div className="flex justify-center">
                <div className="w-16 h-16 bg-secondary rounded-full flex items-center justify-center">
                  <FileText className="h-8 w-8 text-muted-foreground" />
                </div>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-foreground">No specifications found</h3>
                <p className="text-muted-foreground mt-1">
                  {search || categoryFilter !== 'all' || typeFilter !== 'all'
                    ? 'Try adjusting your filters'
                    : 'Create your first specification template to get started'}
                </p>
              </div>
              {!search && categoryFilter === 'all' && typeFilter === 'all' && (
                <Button onClick={handleCreate} className="gap-2">
                  <Plus className="h-4 w-4" />
                  Create Your First Specification
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Type</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="text-right">Usage Count</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedSpecs.map((spec) => (
                  <TableRow
                    key={spec.id}
                    className="cursor-pointer hover:bg-secondary/50"
                    onClick={() => handleRowClick(spec)}
                  >
                    <TableCell>
                      {(spec as any).specification_document_id ? (
                        <Badge variant="outline" className="flex items-center gap-1 w-fit">
                          <FileText className="h-3 w-3" />
                          Document
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="flex items-center gap-1 w-fit">
                          <FileEdit className="h-3 w-3" />
                          Form
                        </Badge>
                      )}
                    </TableCell>
                     <TableCell className="font-medium">
                      {spec.specification_name}
                      {(spec as any).is_standard && (
                        <Badge variant="default" className="ml-2">Standard</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">
                        {spec.category?.replace('_', ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {format(new Date(spec.created_at), 'MMM d, yyyy')}
                    </TableCell>
                    <TableCell className="text-right text-muted-foreground">
                      {spec.usage_count || 0}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => handleEdit(spec, e)}
                          className="h-8 w-8 p-0"
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleClone(spec);
                          }}
                          className="h-8 w-8 p-0"
                        >
                          <Copy className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            setDeletingSpec(spec);
                          }}
                          className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      <SpecificationDialog
        open={isDialogOpen}
        onOpenChange={handleDialogClose}
        specification={editingSpec}
        mode={viewMode}
        onModeChange={setViewMode}
        isAdminView={isAdminView}
      />

      <DeleteSpecificationDialog
        open={!!deletingSpec}
        onOpenChange={(open) => !open && setDeletingSpec(null)}
        specification={deletingSpec}
      />
    </div>
  );
};
